/* Reconciliation — the FIFTH primitive surface (locked 2026-08-31).
 *
 * Owns the "to reconcile" queue, sequenced after bookings: a card
 * stream of reconcile decks (violet stage), one break per card, the
 * engine's one proposal per card — no break table, no always-on ledger
 * (the ledger moves to the fund Record tab per the 3-tab ruling).
 * Breaks folded into a live document deck still resolve inline on that
 * deck in Record Keeping; this stream holds the standalone ones.
 *
 * Commitment breaks flow like any other (the single-position-type
 * guard fell with the commitment-diagonal package). */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { HelpDot } from '../guidance/Guidance'
import { useListKeys } from '../keyboard/useListKeys'
import { fetchDeckQueue } from '../rkdecks/api'
import RkDeck from '../rkdecks/RkDeck'
import type { DeckQueueResponse } from '../rkdecks/types'
import { fetchFundLedger, fetchFundLedgers } from '../ledger'
import {
  breakNames,
  openWords,
  queueNoun,
  queueOpen,
  sumLedgers,
  type FundLedger,
  type LedgerTotals,
} from '../ledgerWords'
import FundSelector from '../FundSelector'
import { useFundScope } from '../fundScope'
import { EmptyState, ErrorState, Skeleton } from '../state'
import '../rkdecks/rkdecks.css'
import '../canon.css'

export default function ReconciliationSurface() {
  const [params] = useSearchParams()
  /* The ONE fund scope (Tier-2 selector ruling): this queue carries the
   * same control every other page carries, in its title. */
  const { fundId, setFund } = useFundScope()
  const taskId = params.get('task')
  const [rows, setRows] = useState<FundLedger[] | null>(null)
  const [queue, setQueue] = useState<DeckQueueResponse | null>(null)
  /* The header's figure is the LEDGER's standing breaks (B1) — snoozed
   * included, breaks folded into a document deck included — the number
   * the sign-off gate and the fund header read. The card stream below
   * stays the deck queue's. */
  const [ledger, setLedger] = useState<LedgerTotals | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [paneled, setPaneled] = useState(false)
  // Retry (state system): a fresh read and a fresh heartbeat clock.
  const [tick, setTick] = useState(0)

  /* `background === true` only from the heartbeat below — the idle clock
   * (lib/api.ts) must not move on a timer. */
  const load = useCallback(
    async (background = false) => {
      try {
        setQueue(await fetchDeckQueue(fundId, background === true))
        setLedger(
          fundId
            ? sumLedgers([await fetchFundLedger(fundId, background === true)])
            : sumLedgers((await fetchFundLedgers(background === true)).funds)
        )
        setError(null)
      } catch (err: unknown) {
        setError(err)
      }
    },
    [fundId]
  )

  useEffect(() => {
    load()
    const t = setInterval(() => load(true), 15000)
    return () => clearInterval(t)
  }, [load, tick])
  const retry = () => {
    setError(null)
    setQueue(null)
    setTick((t) => t + 1)
  }

  /* The selector's rows: every fund, each with THIS queue's own figure. */
  useEffect(() => {
    let cancelled = false
    fetchFundLedgers()
      .then((l) => {
        if (!cancelled) setRows(l.funds)
      })
      .catch(() => {
        if (!cancelled) setRows(null)
      })
    return () => {
      cancelled = true
    }
  }, [])

  /* The stream = every deck at its reconcile stage: standalone
   * reconcile decks plus document decks whose front live card is a
   * break. Stable first-seen order. */
  const decks = useMemo(
    () => (queue?.decks ?? []).filter((d) => d.stage === 'reconciling'),
    [queue]
  )
  const total = decks.length

  const [curId, setCurId] = useState<string | null>(taskId ? `task:${taskId}` : null)
  const curIndex = curId ? decks.findIndex((d) => d.deck_id === curId) : -1
  const [cur, setCur] = useState(0)
  const effIndex = curIndex >= 0 ? curIndex : Math.min(cur, Math.max(0, total - 1))
  const curDeck = decks[effIndex] ?? null

  const nav = useCallback(
    (d: number) => {
      const next = Math.min(Math.max(0, total - 1), Math.max(0, effIndex + d))
      setCur(next)
      setCurId(decks[next]?.deck_id ?? null)
    },
    [total, effIndex, decks]
  )
  /* J / K page the stream — the keyboard canon's one hook (A5). */
  const onNext = useCallback(() => nav(1), [nav])
  const onPrev = useCallback(() => nav(-1), [nav])
  useListKeys({ onNext, onPrev, enabled: total > 0 })

  return (
    <section className={`rkdecks${paneled ? ' paneled' : ''}`} data-testid="recon-surface">
      <div className="inner cnv">
        {/* Same header grammar as every other stream: backlink + the
            surface's canonical uppercase name (perception pass
            2026-08-31 — this page was the odd one out). */}
        <div className="streamhead">
          <Link
            to={`/v2/workbench${fundId ? `?fund=${encodeURIComponent(fundId)}` : ''}`}
            className="backlink"
          >
            ← Workbench
          </Link>
          <h1>
            RECONCILIATION —{' '}
            <FundSelector
              funds={(rows ?? []).map((f) => ({
                id: f.fund_id,
                name: f.fund_name,
                open: queueOpen('reconciliation', f),
                sub: openWords(f),
              }))}
              value={fundId}
              onChoose={setFund}
              noun={queueNoun('reconciliation')}
              loading={rows === null}
            />
            {/* The type-symbol key sits behind the ? (audit cut 4). */}
            <HelpDot k="queue.reconciliation" />
          </h1>
        </div>
        {/* The legend bar is gone (audit cut 4) — its sentence lives behind
            the header's ? above. The bar keeps the LEDGER's figure. */}
        {/* Audit cut 5 (2026-09-04): the bar says only what is there — no
            personified line on its right. The FIGURE is the ledger's. */}
        {ledger !== null && ledger.breaks.open > 0 ? (
          <div className="cn-qbar">
            <span>
              <b data-testid="recon-breaks">{ledger.breaks.open} to reconcile</b>
              {breakNames(ledger.breaks).length
                ? ` — ${breakNames(ledger.breaks).join(' · ')}`
                : ''}
              {ledger.breaks.open > total
                ? ` · ${ledger.breaks.open - total} inside a document deck`
                : ''}
            </span>
          </div>
        ) : null}
        {error ? <ErrorState what="breaks" error={error} onRetry={retry} /> : null}
        {(queue === null || ledger === null) && !error ? (
          <Skeleton kind="cards" n={1} height={300} />
        ) : /* "No open breaks" is the LEDGER's claim, never the stream's — a
              break folded into a document deck leaves the stream empty while
              the record still does not add up. */
        queue !== null && ledger !== null && total === 0 && ledger.breaks.open === 0 ? (
          <EmptyState
            sentence="No open breaks — the record and its movements agree."
            door={{ label: 'Record keeping ›', to: '/v2/workbench/record-keeping' }}
            testId="recon-empty"
          />
        ) : null}
        {total > 0 ? (
          <>
            <div className="pager">
              <button
                type="button"
                className="btn"
                onClick={() => nav(-1)}
                disabled={effIndex === 0}
              >
                ‹ Prev
              </button>
              <span className="pcount" data-testid="recon-pcount">
                break {effIndex + 1} of {total}
                {curDeck?.title ? ` · ${curDeck.title}` : ''}
              </span>
              <button
                type="button"
                className="btn"
                onClick={() => nav(1)}
                disabled={effIndex >= total - 1}
              >
                Next ›
              </button>
            </div>
            {curDeck ? (
              <RkDeck
                key={curDeck.deck_id}
                deckId={curDeck.deck_id}
                onPaneToggle={setPaneled}
                onDeckSettled={() => setTimeout(load, 900)}
                onDecision={load}
              />
            ) : null}
          </>
        ) : null}
      </div>
    </section>
  )
}
